import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, Select, TextInput, useAdminGet } from '../kit';
import { Delta, fmtN, fmtP, monthDays, monthLabel, rate, utcDay, utcStamp } from '../monthly';

/**
 * Homepage searches → Month over month (owner, 2026-10-01: "Unique visitors >
 * Searched Cosmos Mini (keyword vs guided) > Searched and Signed Up", a simple
 * table for the last 5-6 months).
 *
 * Backed by search/home_search_monthly (admin_home_searches.monthly), which
 * counts unique visitors per UTC month at every stage: signed-out homepage
 * visitors, those who searched (Keyword = typed, Guided = a chip or Surprise
 * me — one visitor can do both), and those whose search ended in a new
 * account. Visitors come from PostHog before our tracker's start (2026-09-08)
 * and from our tracker after, unless another source is picked. Its filters are
 * its own: the list's outcome / query filters are per search and would bend
 * the funnel. The month still running is compared with the same stretch of
 * the month before.
 */

export interface MonthRow {
  month: string; running: boolean; through: string | null;
  /** Set on the month the record starts in: every step counts from then. */
  starts: string | null;
  visitors: number | null; visitor_source: string | null; search_source: string;
  searches: number; searched: number; keyword: number; guided: number; both: number; unknown_only: number;
  signed_up: number; signed_up_keyword: number; signed_up_guided: number;
  accounts: number | null; notes: string[];
}
type SamePeriod = Omit<MonthRow, 'running' | 'notes'>;
interface MonthlyData {
  months: MonthRow[]; same_period: SamePeriod | null; available: string[];
  from_month: string; to_month: string; visitors: string; state: string; country: string;
  hide_datacenters: boolean; tracker_start: string | null; notes: string[]; generated_at: string;
}

const SOURCES = ['auto', 'posthog', 'tracker'];
const SOURCE_LABELS: Record<string, string> = {
  auto: 'Same tracker as the searches',
  posthog: 'PostHog, every month',
  tracker: 'Our tracker only (from 8 Sep)',
};
const VSRC_LABELS: Record<string, string> = {
  posthog: 'PostHog', tracker: 'our tracker', split: 'PostHog + our tracker',
};
const STATES = ['anonymous', 'all'];
const STATE_LABELS: Record<string, string> = { anonymous: 'Anonymous searches', all: 'All searches' };

interface Rates { search_rate: number | null; signup_rate: number | null; overall_rate: number | null }
/** One month's numbers, plus the rates. */
const withRates = <T extends SamePeriod>(r: T): T & Rates => ({
  ...r,
  search_rate: rate(r.searched, r.visitors),
  signup_rate: rate(r.signed_up, r.searched),
  overall_rate: rate(r.signed_up, r.visitors),
});
type Row = MonthRow & Rates;
type Base = SamePeriod & Rates;

/** Whether two periods were counted the same way, per kind of step: '' when
 *  they were, else why not. Counts need the same tracker on both sides and
 *  whole months; a rate only needs each side to pair its searches with
 *  visitors from the same tracker (the default). */
type Like = Record<'visitors' | 'searches' | 'rates' | 'accounts', string>;
const SAME: Like = { visitors: '', searches: '', rates: '' , accounts: '' };
const likeness = (a: Base, b: Base): Like => {
  const name = (x: string | null) => (x ? VSRC_LABELS[x] || x : 'not counted');
  const partial = a.starts || b.starts ? 'the first month on record starts on its first search, not the 1st' : '';
  const vDiff = a.visitor_source !== b.visitor_source
    ? `visitors counted with ${name(b.visitor_source)}, then ${name(a.visitor_source)}` : '';
  const sDiff = a.search_source !== b.search_source
    ? `searches from ${name(b.search_source)}, then ${name(a.search_source)}` : '';
  const paired = (x: Base) => x.visitor_source === x.search_source;
  return {
    visitors: partial || vDiff,
    searches: partial || sDiff,
    rates: (!vDiff && !sDiff) || (paired(a) && paired(b)) ? '' : vDiff || sDiff,
    accounts: partial,
  };
};

// ---------------------------------------------------------------------------
// The funnel of one month
// ---------------------------------------------------------------------------
const Stage: React.FC<{
  label: string; n: number | null; width: number | null; tone: string;
  share?: React.ReactNode; delta?: React.ReactNode; sub?: React.ReactNode;
}> = ({ label, n, width, tone, share, delta, sub }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-baseline justify-between gap-2 flex-wrap">
      <span className="text-xs font-bold text-grayscale-1300">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="text-lg font-bold font-grotesk text-grayscale-1300">{fmtN(n)}</span>
        {share && <span className="text-xs text-grayscale-1200">{share}</span>}
        {delta}
      </span>
    </div>
    <div className="h-3 rounded bg-grayscale-200 overflow-hidden">
      {width !== null && <div className={`h-full rounded ${tone}`} style={{ width: n ? `max(3px, ${width * 100}%)` : 0 }} />}
    </div>
    {sub && <div className="text-[11px] text-grayscale-1100">{sub}</div>}
  </div>
);

const Funnel: React.FC<{ row: Row; base: Base | null; vs: string; like: Like; onList?: () => void }> =
  ({ row, base, vs, like, onList }) => {
  const top = row.visitors ?? null;
  const w = (n: number) => (top ? Math.min(1, n / top) : null);
  const src = row.visitor_source ? VSRC_LABELS[row.visitor_source] || row.visitor_source : '';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
          {monthLabel(row.month)}{row.running ? ' · to date' : ''}{row.starts ? ` · from ${utcDay(row.starts)}` : ''}
        </div>
        <div className="text-[11px] text-grayscale-1100">
          {base ? <>change vs {vs}</> : 'no earlier month in range to compare with'}
        </div>
      </div>
      <Stage label="Homepage visitors" n={row.visitors} width={top === null ? null : 1} tone="bg-primary-300"
        share={src ? <span className="text-[11px] text-grayscale-1100">via {src}</span> : undefined}
        delta={<Delta cur={row.visitors} prev={base?.visitors} kind="count" vs={vs} unlike={like.visitors} />}
        sub={row.visitors === null ? 'Not counted for this month with the tracker picked above.' : 'signed out, loaded the homepage'} />
      <Stage label="Searched Cosmos Mini" n={row.searched} width={top === null ? 1 : w(row.searched)} tone="bg-primary-500"
        share={<>{fmtP(row.search_rate)} of visitors <Delta cur={row.search_rate} prev={base?.search_rate} kind="rate" vs={vs} unlike={like.rates} /></>}
        delta={<Delta cur={row.searched} prev={base?.searched} kind="count" vs={vs} unlike={like.searches} />}
        sub={<>
          <span className="inline-block w-2 h-2 rounded-sm bg-primary-700 mr-1" />Keyword (typed) <b>{fmtN(row.keyword)}</b>
          {' · '}<span className="inline-block w-2 h-2 rounded-sm bg-secondary-400 mr-1" />Guided (chip / Surprise me) <b>{fmtN(row.guided)}</b>
          {row.both ? <> · both <b>{fmtN(row.both)}</b></> : null}
          {row.unknown_only ? <> · source not recorded <b>{fmtN(row.unknown_only)}</b></> : null}
        </>} />
      <Stage label="Searched and signed up" n={row.signed_up}
        width={top === null ? (row.searched ? row.signed_up / row.searched : 0) : w(row.signed_up)} tone="bg-green-600"
        share={<>{fmtP(row.signup_rate)} of searchers <Delta cur={row.signup_rate} prev={base?.signup_rate} kind="rate" vs={vs} unlike={like.rates} /></>}
        delta={<Delta cur={row.signed_up} prev={base?.signed_up} kind="count" vs={vs} unlike={like.searches} />}
        sub={<>
          after a keyword search <b>{fmtN(row.signed_up_keyword)}</b> · after a guided one <b>{fmtN(row.signed_up_guided)}</b>
          {row.visitors ? <> · {fmtP(row.overall_rate)} of all homepage visitors</> : null}
          {row.accounts !== null && row.accounts !== undefined
            ? <> · {fmtN(row.accounts)} new accounts in all that signed in ({row.accounts ? fmtP(Math.min(1, row.signed_up / row.accounts)) : '—'} right after a homepage search)</>
            : null}
        </>} />
      {onList && (
        <div><Btn small onClick={onList}>List this month’s searches ↓</Btn></div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The trend: one column (or a pair) per month
// ---------------------------------------------------------------------------
const METRICS: { key: string; label: string; kind: 'count' | 'rate' }[] = [
  { key: 'visitors', label: 'Homepage visitors', kind: 'count' },
  { key: 'searched', label: 'Searched', kind: 'count' },
  { key: 'split', label: 'Keyword vs guided', kind: 'count' },
  { key: 'signed_up', label: 'Signed up', kind: 'count' },
  { key: 'search_rate', label: '% searched', kind: 'rate' },
  { key: 'signup_rate', label: '% of searchers signed up', kind: 'rate' },
];

const Trend: React.FC<{ rows: Row[]; selected: string; onSelect: (m: string) => void }> =
  ({ rows, selected, onSelect }) => {
  const [metric, setMetric] = useState('searched');
  const spec = METRICS.find(m => m.key === metric) || METRICS[1];
  const series = metric === 'split'
    ? [{ key: 'keyword', tone: 'bg-primary-700', label: 'Keyword' }, { key: 'guided', tone: 'bg-secondary-400', label: 'Guided' }]
    : [{ key: metric, tone: 'bg-primary-500', label: spec.label }];
  const val = (r: any, k: string): number | null => (typeof r[k] === 'number' ? r[k] : null);
  const max = Math.max(spec.kind === 'rate' ? 0.0001 : 1, ...rows.flatMap(r => series.map(s => val(r, s.key) ?? 0)));
  const show = (v: number | null) => (spec.kind === 'rate' ? fmtP(v) : fmtN(v));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 flex-wrap">
        {METRICS.map(m => (
          <button key={m.key} type="button" onClick={() => setMetric(m.key)}
            className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${metric === m.key
              ? 'bg-primary-500 text-grayscale-100 font-bold'
              : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            {m.label}
          </button>
        ))}
      </div>
      {metric === 'split' && (
        <div className="flex gap-3 text-[11px] text-grayscale-1100">
          {series.map(s => <span key={s.key}><span className={`inline-block w-2 h-2 rounded-sm mr-1 ${s.tone}`} />{s.label}</span>)}
          <span>(a visitor who did both is in both)</span>
        </div>
      )}
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-10 shrink-0 h-44 pt-4">
          <span>{show(max)}</span><span>0</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative h-44 border-b border-grayscale-300">
            <div aria-hidden className="absolute inset-x-0 top-4 border-t border-dashed border-grayscale-200" />
            <div className="absolute inset-0 flex items-end gap-2">
              {rows.map(r => (
                <button key={r.month} type="button" onClick={() => onSelect(r.month)}
                  title={`${monthLabel(r.month)}: ${series.map(s => `${s.label} ${show(val(r, s.key))}`).join(' · ')}`}
                  className={`flex-1 h-full flex items-end justify-center gap-[3px] px-1 rounded-t ${r.month === selected ? 'bg-primary-100' : 'hover:bg-grayscale-200/60'}`}>
                  {series.map(s => {
                    const v = val(r, s.key);
                    return (
                      <div key={s.key} className="flex-1 max-w-[2.5rem] h-full flex flex-col justify-end items-center">
                        <span className="text-[10px] text-grayscale-1200 leading-tight mb-0.5">{show(v)}</span>
                        <div className={`w-full rounded-t ${s.tone} ${r.running ? 'opacity-60' : ''}`}
                          style={{ height: v ? `max(2px, calc((100% - 1rem) * ${v / max}))` : 0 }} />
                      </div>
                    );
                  })}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 text-[10px] text-grayscale-1100 mt-1">
            {rows.map(r => (
              <span key={r.month} className={`flex-1 text-center ${r.month === selected ? 'font-bold text-grayscale-1300' : ''}`}>
                {monthLabel(r.month, true)}{r.running ? '*' : ''}
              </span>
            ))}
          </div>
        </div>
      </div>
      {rows.some(r => r.running) && (
        <div className="text-[10px] text-grayscale-1100">* month to date (paler column)</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------
const HomeMonthly: React.FC<{ tick: number; onShowMonth?: (from: string, to: string) => void }> =
  ({ tick, onShowMonth }) => {
  const [fromM, setFromM] = useState('');
  const [toM, setToM] = useState('');
  const [source, setSource] = useState('auto');
  const [who, setWho] = useState('anonymous');
  const [countryIn, setCountryIn] = useState('');
  const [country, setCountry] = useState('');
  const [hideDc, setHideDc] = useState(false);
  const [selected, setSelected] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCountry(countryIn.trim()), 400);
    return () => clearTimeout(t);
  }, [countryIn]);

  const params = useMemo(() => {
    const p: Record<string, string> = { visitors: source, state: who };
    if (fromM) p.from_month = fromM;
    if (toM) p.to_month = toM;
    if (country) p.country = country;
    if (hideDc) p.hide_datacenters = '1';
    return p;
  }, [fromM, toM, source, who, country, hideDc]);
  const key = JSON.stringify(params);
  const q = useAdminGet<MonthlyData>('search', 'home_search_monthly', params, [key, tick]);
  const d = q.data;

  const rows: Row[] = useMemo(() => (d?.months || []).map(r => withRates(r)), [d]);
  const same = d?.same_period ? withRates(d.same_period) : null;
  // What each month is compared with: the month before, or for the month
  // still running, the same stretch of the month before.
  const baseOf = (i: number): { base: Base | null; vs: string; like: Like } => {
    const r = rows[i];
    if (!r || i === 0) return { base: null, vs: '', like: SAME };
    if (r.running && same && same.month === rows[i - 1].month) {
      return { base: same, vs: `${monthLabel(same.month)} to ${utcStamp(same.through)} (same stretch)`, like: likeness(r, same) };
    }
    return { base: rows[i - 1], vs: monthLabel(rows[i - 1].month), like: likeness(r, rows[i - 1]) };
  };
  // Default to the last full month: a day-old month says little.
  const fallback = rows.length ? (rows[rows.length - 1].running && rows.length > 1 ? rows[rows.length - 2] : rows[rows.length - 1]).month : '';
  const sel = rows.some(r => r.month === selected) ? selected : fallback;
  const selIdx = rows.findIndex(r => r.month === sel);

  const tableRows = rows.map((r, i) => ({
    month: r.month,
    visitors: r.visitors,
    searched: r.searched,
    search_rate_pct: r.search_rate === null ? null : +(r.search_rate * 100).toFixed(1),
    keyword: r.keyword,
    guided: r.guided,
    signed_up: r.signed_up,
    signup_rate_pct: r.signup_rate === null ? null : +(r.signup_rate * 100).toFixed(1),
    overall_rate_pct: r.overall_rate === null ? null : +(r.overall_rate * 100).toFixed(2),
    accounts: r.accounts,
    _i: i,
  }));
  const cell = (key: keyof Base, kind: 'count' | 'rate', group: keyof Like) => (row: Record<string, any>) => {
    const r = rows[row._i];
    const { base, vs, like } = baseOf(row._i);
    const v = r[key] as number | null;
    return (
      <div className="flex flex-col leading-tight">
        <span>{kind === 'rate' ? fmtP(v) : fmtN(v)}</span>
        <Delta cur={v} prev={base ? (base[key] as number | null) : null} kind={kind} vs={vs} unlike={like[group]} />
      </div>
    );
  };

  const opts = d?.available || [];
  const monthOpts = ['', ...opts];
  const monthLabels: Record<string, string> = { '': '6 months back' };
  opts.forEach(m => { monthLabels[m] = monthLabel(m); });
  const busy = q.loading && !d;
  const changed = fromM || toM || source !== 'auto' || who !== 'anonymous' || country || hideDc;

  return (
    <Section title="Month over month"
      right={<span className="text-[11px] text-grayscale-1100">UTC months · unique visitors at every step</span>}>
      <p className="text-xs text-grayscale-1100 max-w-4xl -mt-1">
        Signed-out homepage visitors → those who searched Cosmos Mini (keyword = typed, guided = a suggestion chip or
        Surprise me) → those whose search ended in a new account in the same visit.{' '}
        {source === 'auto' && <>Searches and visitors come from PostHog until our own tracker started (8 Sep 2026) and
          from our tracker after, so each month’s rates compare the two from the same source. </>}
        {source === 'posthog' && <>Visitors are PostHog’s in every month; searches after 8 Sep come from our tracker. </>}
        {source === 'tracker' && <>Only what our tracker saw (from 8 Sep 2026): earlier months show searches but no visitors. </>}
        Bots, our dev / preview sites and staff are left out. Click a month for its funnel.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1200">From
          <Select value={fromM} onChange={setFromM} options={monthOpts} labels={monthLabels} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1200">To
          <Select value={toM} onChange={setToM} options={monthOpts} labels={{ ...monthLabels, '': 'This month' }} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Count visitors with
          <Select value={source} onChange={setSource} options={SOURCES} labels={SOURCE_LABELS} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Searches
          <Select value={who} onChange={setWho} options={STATES} labels={STATE_LABELS} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1200">Country
          <TextInput value={countryIn} onChange={setCountryIn} placeholder="e.g. India or IN" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 h-9"
          title="Our tracker marks visits from cloud / hosting networks; PostHog can't, so this only changes months our tracker counts">
          <input type="checkbox" checked={hideDc} onChange={e => setHideDc(e.target.checked)} />
          Hide datacenter visits
        </label>
        {changed && (
          <Btn small onClick={() => { setFromM(''); setToM(''); setSource('auto'); setWho('anonymous'); setCountryIn(''); setCountry(''); setHideDc(false); }}>
            Reset
          </Btn>
        )}
        {q.loading && d && <span className="text-[11px] text-grayscale-1100 h-9 flex items-center">Updating…</span>}
      </div>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {(d?.notes || []).map(n => <div key={n} className="text-xs rounded-lg px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800">{n}</div>)}
      {busy ? <Loading label="Counting visitors… (PostHog can take a few seconds)" /> : !rows.length ? null : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-grayscale-300 p-3">
              {selIdx >= 0 && (
                <Funnel row={rows[selIdx]} {...baseOf(selIdx)}
                  onList={onShowMonth ? () => {
                    const [a, b] = monthDays(rows[selIdx].month);
                    onShowMonth(a, b);
                  } : undefined} />
              )}
            </div>
            <div className="rounded-lg border border-grayscale-300 p-3">
              <Trend rows={rows} selected={sel} onSelect={setSelected} />
            </div>
          </div>
          <DataTable rows={tableRows}
            columns={['month', 'visitors', 'searched', 'search_rate_pct', 'keyword', 'guided', 'signed_up',
              'signup_rate_pct', 'overall_rate_pct', 'accounts']}
            labels={{
              month: 'Month', visitors: 'Homepage visitors', searched: 'Searched', search_rate_pct: 'Search rate',
              keyword: 'Keyword', guided: 'Guided', signed_up: 'Signed up',
              signup_rate_pct: 'Sign-up rate', overall_rate_pct: 'Visitor → sign-up',
              accounts: 'All new accounts',
            }}
            csvName={`homepage-search-funnel-${d?.from_month || ''}-to-${d?.to_month || ''}`}
            maxHeight="none"
            render={{
              month: row => {
                const r = rows[row._i];
                return (
                  <button type="button" onClick={() => setSelected(r.month)}
                    className={`text-left whitespace-nowrap underline-offset-2 hover:underline ${r.month === sel ? 'font-bold text-grayscale-1300' : ''}`}>
                    {monthLabel(r.month)}
                    {r.running && <span className="ml-1 text-[10px] rounded border border-grayscale-400 px-1 text-grayscale-1100">to date</span>}
                    {r.starts && <span className="ml-1 text-[10px] rounded border border-grayscale-400 px-1 text-grayscale-1100">from {utcDay(r.starts)}</span>}
                    {r.notes.length > 0 && <span className="ml-1 text-grayscale-1100 cursor-help" title={r.notes.join('\n')}>ⓘ</span>}
                  </button>
                );
              },
              visitors: cell('visitors', 'count', 'visitors'),
              searched: cell('searched', 'count', 'searches'),
              search_rate_pct: cell('search_rate', 'rate', 'rates'),
              keyword: cell('keyword', 'count', 'searches'),
              guided: cell('guided', 'count', 'searches'),
              signed_up: cell('signed_up', 'count', 'searches'),
              signup_rate_pct: cell('signup_rate', 'rate', 'rates'),
              overall_rate_pct: cell('overall_rate', 'rate', 'rates'),
              accounts: cell('accounts', 'count', 'accounts'),
            }} />
          {rows.some(r => r.notes.length) && (
            <ul className="text-[11px] text-grayscale-1100 list-disc pl-5">
              {rows.flatMap(r => r.notes.map(n => <li key={r.month + n}><b>{monthLabel(r.month)}:</b> {n}</li>))}
            </ul>
          )}
          <div className="text-[11px] text-grayscale-1100">
            Search rate = searched ÷ homepage visitors; sign-up rate = signed up ÷ searched; visitor → sign-up = signed up ÷
            homepage visitors. All new accounts = accounts created that month, by any route, that have signed in at
            least once (admin imports and unfinished sign-ups are left out). ~ marks a change between months counted
            differently (hover it for why).
            Keyword and guided overlap: a visitor who did both counts in each. “Signed up” is an account created in the
            same visit, within 30 minutes of a search (or before the next one) — a login to an existing account isn’t one.
            Monthly uniques don’t add up across months. ▲▼ compare with the month before;
            the month still running is compared with the same stretch of the month before.
          </div>
        </>
      )}
    </Section>
  );
};

export default HomeMonthly;
